import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import Cookies from "js-cookie";
import { notification, Spin, Select, Button, Card, Divider } from "antd";
import moment from "moment";
import {
    HiOutlineSave,
    HiOutlineChevronLeft,
    HiOutlineInformationCircle,
    HiOutlineClock
} from 'react-icons/hi';

import { encryptData, decryptData } from "../Crypto_Helpers/cryptoHelper";
import { initEncryptionSession } from "../../encryptionSession";

const { Option } = Select;

/* ── Inline time input component ─────────────────────────── */
const TimeInput = ({ label, value, onChange, placeholder }) => {
    return (
        <div>
            <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-3 px-1">
                {label}
            </label>
            <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-teal-500 pointer-events-none">
                    <HiOutlineClock size={20} />
                </span>
                <input
                    type="time"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className="w-full pl-11 pr-4 text-gray-800 font-bold text-sm
                               rounded-2xl border border-gray-100 bg-gray-50/50
                               hover:bg-white hover:border-teal-200
                               focus:outline-none focus:border-teal-500 focus:bg-white focus:ring-2 focus:ring-teal-100
                               transition-all cursor-pointer"
                    style={{ height: '56px', fontSize: '15px' }}
                />
            </div>
            {value && (
                <p className="mt-1.5 text-xs text-teal-500 font-bold px-1">
                    {moment(value, 'HH:mm').format('hh:mm A')}
                </p>
            )}
        </div>
    );
};

const EditWorkingHoursComponent = () => {
    const { workingHoursId } = useParams();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);

    // Data states
    const [services, setServices] = useState([]);
    const [appointmentTypes, setAppointmentTypes] = useState([]);

    // Form states — times stored as "HH:mm" strings
    const [selectedService, setSelectedService] = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    const [selectedDays, setSelectedDays] = useState([]);
    const [editstartTime, setEditStartTime] = useState('');
    const [editendTime, setEditEndTime] = useState('');

    const days = [
        { label: 'Sun', value: 1 },
        { label: 'Mon', value: 2 },
        { label: 'Tue', value: 3 },
        { label: 'Wed', value: 4 },
        { label: 'Thu', value: 5 },
        { label: 'Fri', value: 6 },
        { label: 'Sat', value: 7 },
    ];

    useEffect(() => {
        fetchInitialDataAndRecord();
    }, []);

    const fetchInitialDataAndRecord = async () => {
        setLoading(true);
        try {
            const doctorId = Cookies.get("doctor_id") || Cookies.get("Doctor_id");
            const clinicId = Cookies.get("clinic_id") || Cookies.get("Clinic_id") || 0;
            const aesKey = await initEncryptionSession();

            const [servicesRes, typesRes, recordRes] = await Promise.all([
                axiosInstance.post("doctor/getDoctorService", { doctor_id: doctorId }),
                axiosInstance.get("doctor/get_mas_appointment_type"),
                axiosInstance.post("doctor/getdocAppointmentSettings", {
                    doctorId: doctorId,
                    clinicId: clinicId,
                    limit: 100,
                    pageno: 1
                })
            ]);

            const serviceList = servicesRes.data.data || [];
            const typeList = typesRes.data.data || [];
            setServices(serviceList);
            setAppointmentTypes(typeList);

            let rawDetails = recordRes.data.data?.[0]?.details || recordRes.data.data || [];
            let decryptedRecords = rawDetails;

            if (typeof rawDetails === 'string') {
                decryptedRecords = await decryptData(rawDetails, aesKey);
            }

            const workingHour = decryptedRecords.find((x) => String(x.id) === String(workingHoursId));

            if (!workingHour) {
                notification.error({ message: "Working hours record not found" });
                history.goBack();
                return;
            }

            const mappedService = serviceList.find(s => String(s.id) === String(workingHour.service_id));
            const mappedType = typeList.find(t => String(t.id) === String(workingHour.appointtype_id));

            setSelectedService(mappedService);
            setSelectedType(mappedType || { id: workingHour.appointtype_id, appointment_type: workingHour.appointment_type });
            setSelectedDays(Array.isArray(workingHour.day) ? workingHour.day.map(Number) : String(workingHour.day).split(',').map(Number));
            // Convert "HH:mm:ss" → "HH:mm" for input[type=time]
            setEditStartTime(workingHour.from_time ? workingHour.from_time.substring(0, 5) : '');
            setEditEndTime(workingHour.to_time ? workingHour.to_time.substring(0, 5) : '');

        } catch (err) {
            console.error("Failed to load data", err);
            notification.error({ message: "Failed to load services or existing schedule" });
        } finally {
            setLoading(false);
        }
    };

    // Calculate total slots for summary
    const calcSlots = () => {
        if (!editstartTime || !editendTime || !selectedService?.slot_duration) return 0;
        const start = moment(editstartTime, 'HH:mm');
        const end = moment(editendTime, 'HH:mm');
        if (!end.isAfter(start)) return 0;

        const durationString = String(selectedService.slot_duration);
        let durationInMinutes = 0;
        if (durationString.includes(':')) {
            const parts = durationString.split(':').map(Number);
            durationInMinutes = (parts[0] * 60) + parts[1];
        } else {
            durationInMinutes = parseInt(durationString);
        }
        if (durationInMinutes <= 0) return 0;

        return Math.floor(end.diff(start, 'minutes') / durationInMinutes);
    };

    const handleUpdate = async () => {
        if (!selectedService || !selectedType || selectedDays.length === 0 || !editstartTime || !editendTime) {
            notification.warning({ message: "Please fill all required fields" });
            return;
        }

        const start = moment(editstartTime, 'HH:mm');
        const end = moment(editendTime, 'HH:mm');

        if (!start.isBefore(end)) {
            notification.warning({ message: "End time must be after start time" });
            return;
        }

        const durationString = String(selectedService.slot_duration);
        let durationInMinutes = 0;
        if (durationString.includes(':')) {
            const parts = durationString.split(":").map(Number);
            durationInMinutes = (parts[0] * 60) + parts[1];
        } else {
            durationInMinutes = Number(durationString);
        }

        const noOfSlots = Math.floor(end.diff(start, "minutes") / durationInMinutes);

        if (noOfSlots <= 0) {
            notification.error({ message: "Invalid time range for the selected service duration" });
            return;
        }

        setSaveLoading(true);
        try {
            const payload = {
                id: workingHoursId,
                clinicId: Cookies.get("clinic_id") || Cookies.get("Clinic_id") || 0,
                doctorId: Cookies.get("doctor_id") || Cookies.get("Doctor_id"),
                serviceId: selectedService.id,
                fromtime: `${editstartTime}:00`,
                totime: `${editendTime}:00`,
                slotduration: selectedService.slot_duration,
                NoOfslots: noOfSlots,
                appointmentType: selectedType.id,
                modifiedby: Cookies.get("doctor_id") || Cookies.get("Doctor_id"),
                ipaddress: "127.0.0.1",
                days: selectedDays,
                specialityId: selectedService.specid || selectedService.speciality_id || selectedService.specialityId || Cookies.get("speciality_Id") || 0
            };

            const response = await axiosInstance.put("doctor/editdocAppointmentSettings", payload);

            if (response.data.status === 1) {
                notification.success({ message: "Working hours updated successfully" });
                history.push("/dashboard/workinghours");
            } else {
                notification.error({ message: response.data.msg || "Failed to update schedule" });
            }
        } catch (err) {
            console.error("Update failed", err);
            notification.error({ message: "An error occurred while saving changes" });
        } finally {
            setSaveLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="h-96 flex items-center justify-center">
                <Spin size="large" tip="Loading schedule data..." />
            </div>
        );
    }

    return (
        <div className="w-full space-y-4 animate-in fade-in duration-300 text-gray-800">
            {/* Header Bar */}
            <div className="bg-white px-5 py-3.5 rounded-xl border border-gray-200 shadow-2xs flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <button
                        onClick={() => history.goBack()}
                        className="p-1 hover:bg-gray-100 rounded text-gray-400 transition-colors cursor-pointer"
                    >
                        <HiOutlineChevronLeft size={18} />
                    </button>
                    <div>
                        <h2 className="text-sm font-bold text-gray-800 leading-tight">Edit Working Hours</h2>
                        <p className="text-gray-400 text-[11px]">Modify your existing availability for patient bookings</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                {/* Settings Form */}
                <div className="lg:col-span-2 space-y-3">
                    <Card className="rounded-lg border border-gray-200 shadow-2xs p-1 sm:p-2">
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1">Medical Service</label>
                                    <Select
                                        className="w-full"
                                        style={{ height: '32px' }}
                                        placeholder="Select service"
                                        value={selectedService?.id}
                                        onChange={(val) => setSelectedService(services.find(s => s.id === val))}
                                    >
                                        {services.map(s => (
                                            <Option key={s.id} value={s.id}>
                                                <div className="flex justify-between items-center w-full text-xs">
                                                    <span className="font-semibold">{s.service}</span>
                                                    <span className="text-[10px] text-gray-400">({s.slot_duration}m)</span>
                                                </div>
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1">Appointment Type</label>
                                    <Select
                                        className="w-full"
                                        style={{ height: '32px' }}
                                        placeholder="Select type"
                                        value={selectedType?.id}
                                        onChange={(val) => setSelectedType(appointmentTypes.find(t => t.id === val))}
                                    >
                                        {appointmentTypes.map(t => (
                                            <Option key={t.id} value={t.id}>{t.appointment_type}</Option>
                                        ))}
                                    </Select>
                                </div>
                            </div>

                            <Divider className="my-1" />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <TimeInput
                                    label="Start Time"
                                    value={editstartTime}
                                    onChange={setEditStartTime}
                                    placeholder="Select Start Time"
                                />
                                <TimeInput
                                    label="End Time"
                                    value={editendTime}
                                    onChange={setEditEndTime}
                                    placeholder="Select End Time"
                                />
                            </div>

                            {/* Working window preview */}
                            {editstartTime && editendTime && (
                                <div className="bg-teal-50 border border-teal-100 rounded-md px-3 py-2 flex items-center gap-2">
                                    <HiOutlineClock className="text-teal-600 shrink-0" size={16} />
                                    <span className="text-xs text-teal-800 font-semibold">
                                        {moment(editstartTime, 'HH:mm').format('hh:mm A')} → {moment(editendTime, 'HH:mm').format('hh:mm A')}
                                        {calcSlots() > 0 && (
                                            <span className="ml-2 text-teal-600 font-normal">
                                                ({calcSlots()} slots of {selectedService?.slot_duration} min)
                                            </span>
                                        )}
                                    </span>
                                </div>
                            )}

                            <Divider className="my-1" />

                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="block text-xs font-semibold text-gray-700">Repeat On</label>
                                    <Button
                                        type="link"
                                        size="small"
                                        className="text-teal-600 font-semibold p-0 text-xs"
                                        onClick={() => setSelectedDays(selectedDays.length === 7 ? [] : [1, 2, 3, 4, 5, 6, 7])}
                                    >
                                        {selectedDays.length === 7 ? 'Deselect All' : 'Select All Days'}
                                    </Button>
                                </div>
                                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                                    {days.map(day => (
                                        <button
                                            key={day.value}
                                            type="button"
                                            onClick={() => {
                                                const next = selectedDays.includes(day.value)
                                                    ? selectedDays.filter(d => d !== day.value)
                                                    : [...selectedDays, day.value];
                                                setSelectedDays(next);
                                            }}
                                            className={`py-2 rounded-lg text-xs font-bold transition-all border cursor-pointer ${
                                                selectedDays.includes(day.value)
                                                    ? 'bg-teal-600 border-teal-600 text-white shadow-2xs'
                                                    : 'bg-white border-gray-200 text-gray-500 hover:border-teal-300 hover:text-teal-600'
                                            }`}
                                        >
                                            {day.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Summary Card */}
                <div className="space-y-3">
                    <Card className="rounded-lg border border-gray-200 shadow-2xs bg-gray-50/50 p-1 sm:p-2">
                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Schedule Overview</h3>

                        <div className="space-y-2.5">
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-gray-500">Service</span>
                                <span className="font-bold text-gray-800 text-right max-w-[60%]">{selectedService?.service || '--'}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-gray-500">Type</span>
                                <span className="font-bold text-gray-800">{selectedType?.appointment_type || '--'}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-gray-500">Working Window</span>
                                <span className="font-bold text-gray-800 text-right">
                                    {editstartTime && editendTime
                                        ? `${moment(editstartTime, 'HH:mm').format('hh:mm A')} - ${moment(editendTime, 'HH:mm').format('hh:mm A')}`
                                        : '--'}
                                </span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-gray-500">Slot Size</span>
                                <span className="font-bold text-teal-600">
                                    {selectedService ? `${selectedService.slot_duration} mins` : '--'}
                                </span>
                            </div>
                            <div className="flex justify-between items-center pt-2 border-t border-gray-200 mt-1">
                                <span className="text-teal-700 font-bold text-xs">Total Daily Slots</span>
                                <span className="text-xl font-black text-teal-700">{calcSlots()}</span>
                            </div>
                        </div>

                        <Divider className="my-3" />

                        <div className="space-y-2.5">
                            <div className="bg-blue-50 p-2.5 rounded-lg flex gap-2">
                                <HiOutlineInformationCircle className="text-blue-500 shrink-0 mt-0.5" size={16} />
                                <p className="text-[11px] text-blue-700 font-medium leading-normal">
                                    Updates will be applied immediately to the recurring schedule.
                                </p>
                            </div>

                            <Button
                                type="primary"
                                size="middle"
                                block
                                className="h-9 rounded-lg bg-teal-600 hover:bg-teal-700 border-none font-bold text-xs shadow-2xs cursor-pointer"
                                loading={saveLoading}
                                onClick={handleUpdate}
                                icon={<HiOutlineSave size={15} />}
                            >
                                Update Schedule
                            </Button>
                        </div>
                    </Card>
                </div>
            </div>

            <style jsx="true">{`
                .custom-select-large .ant-select-selector {
                    height: 56px !important;
                    border-radius: 16px !important;
                    padding: 0 16px !important;
                    display: flex !important;
                    align-items: center !important;
                    background-color: #f9fafb !important;
                    border: 1px solid #f3f4f6 !important;
                }
                .custom-select-large .ant-select-selection-placeholder {
                    font-size: 14px;
                    font-weight: 600;
                    color: #9ca3af;
                }
                .custom-select-large .ant-select-selection-item {
                    font-size: 14px;
                    font-weight: 700;
                    color: #1f2937;
                }
            `}</style>
        </div>
    );
};

export default EditWorkingHoursComponent;
